// ─── Phone normalizer (E.164, Indonesia-focused) ─────────────────────────────
//
// Accepts common Indonesian input forms and outputs E.164 without leading `+`:
//
//   "0812-3456-789"      → "628123456789"
//   "+62 812 3456 789"   → "628123456789"
//   "62 812-3456-789"    → "628123456789"
//   "812 345 6789"       → "628123456789"   (assumes Indonesia when no prefix)
//
// Used by:
//   - Contact create/update (write side): store E.164 form
//   - Phone-OTP login lookup: query contacts where phone == E.164(input)
//   - wa.me link generator (Phase 8): URL needs E.164 without `+`
//
// Indonesia-only at MVP. International extension: relax regex + accept
// country code passed explicitly. Track in plan when EPR/OKR onboards
// non-Indonesia users.

/** Phone validation regex for Indonesia. After normalize, valid string matches
 *  `^62` followed by 9–13 digits (Indonesian mobile + landline range). */
export const INDONESIA_PHONE_REGEX = /^62[0-9]{9,13}$/;

/** Normalize phone input to E.164 format without leading `+`.
 *  Returns `null` on invalid input. */
export function normalizePhone(input: string | null | undefined): string | null {
  if (!input || typeof input !== 'string') return null;

  const digits = input.replace(/\D/g, '');
  if (!digits) return null;

  let normalized: string;
  if (digits.startsWith('0')) {
    normalized = '62' + digits.slice(1);
  } else if (digits.startsWith('62')) {
    normalized = digits;
  } else if (digits.length >= 8 && digits.length <= 14) {
    // Assume Indonesia when no country code prefix supplied.
    normalized = '62' + digits;
  } else {
    return null;
  }

  if (!INDONESIA_PHONE_REGEX.test(normalized)) return null;
  return normalized;
}

/** True if `normalizePhone(input)` would succeed. */
export function isValidPhone(input: string | null | undefined): boolean {
  return normalizePhone(input) !== null;
}

/** Display formatter: `"628123456789"` → `"+62 812-3456-789"`.
 *  Returns the input unchanged if normalization fails. */
export function formatPhoneDisplay(input: string): string {
  const norm = normalizePhone(input);
  if (!norm) return input;
  const rest = norm.slice(2);
  // Group as 3-4-rest (covers typical Indonesian 10-12 digit mobile).
  const a = rest.slice(0, 3);
  const b = rest.slice(3, 7);
  const c = rest.slice(7);
  return `+62 ${a}-${b}${c ? '-' + c : ''}`;
}
